import { Component } from '@theme/component';

/**
 * Resolves once the page has finished loading (fonts included) and the main
 * thread is idle, so a marquee never starts moving while the page is still
 * busy (that dropped frames and looked like a jump). Never waits more than 4s.
 */
function whenPageSettled() {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    setTimeout(finish, 4000);
    const loaded = document.readyState === 'complete'
      ? Promise.resolve()
      : new Promise((r) => window.addEventListener('load', () => r(), { once: true }));
    Promise.all([loaded, document.fonts?.ready ?? Promise.resolve()]).then(() => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(finish, { timeout: 1000 });
      } else {
        setTimeout(finish, 200);
      }
    }, finish);
  });
}

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /** @type {HTMLElement | null} */
  #marqueeTrack = null;
  /** @type {HTMLElement[]} */
  #marqueeOriginals = [];
  /** @type {number} */
  #marqueeOriginalsWidth = 0;
  /** @type {ResizeObserver | null} */
  #marqueeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#setupMarquee();
    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#marqueeObserver?.disconnect();
    this.#marqueeObserver = null;
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  /**
   * Sets up the mobile marquee. The slides go inside two identical groups
   * (A = originals plus enough copies to cover the screen, B = a copy of A).
   * Each group runs its own animation from +100% to -100% of its width, B half
   * a cycle behind A, so they always sit end to end. A group only restarts
   * when it is completely off screen, so the visitor never sees a restart
   * (a single moving track that jumps back made Safari flash a blank frame,
   * because it had stopped painting the area it jumped back to).
   * On desktop the groups are display: contents (the slides behave as before)
   * and group B is hidden.
   */
  #setupMarquee() {
    const track = this.querySelector('.announcement-bar__slides');
    if (!(track instanceof HTMLElement) || track.dataset.marqueeReady === 'true') return;

    const originals = /** @type {HTMLElement[]} */ (
      Array.from(track.children).filter((el) => el instanceof HTMLElement)
    );
    if (originals.length === 0) return;

    const groupA = document.createElement('div');
    groupA.className = 'announcement-bar__group';
    groupA.dataset.marqueeGroup = 'a';
    for (const slide of originals) groupA.appendChild(slide);
    track.appendChild(groupA);

    this.#marqueeTrack = track;
    this.#marqueeOriginals = originals;
    track.dataset.marqueeReady = 'true';
    this.#syncMarquee();

    // ResizeObserver fires once after initial layout and on every resize.
    this.#marqueeObserver = new ResizeObserver(() => this.#syncMarquee());
    this.#marqueeObserver.observe(this);
    // Start moving only once the page has settled (no jump on load).
    whenPageSettled().then(() =>
      requestAnimationFrame(() => {
        this.#syncMarquee();
        this.#matchSpeed();
        track.dataset.marqueeRunning = 'true';
      })
    );
  }

  /**
   * Makes group A at least as wide as the screen (adding copies of the
   * originals if needed) and keeps group B an exact copy of group A.
   */
  #syncMarquee() {
    const track = this.#marqueeTrack;
    const originals = this.#marqueeOriginals;
    if (!track || originals.length === 0) return;
    const groupA = track.querySelector('[data-marquee-group="a"]');
    if (!(groupA instanceof HTMLElement)) return;

    const originalsWidth = originals.reduce((sum, el) => {
      const style = getComputedStyle(el);
      const marginEnd = parseFloat(style.marginInlineEnd || style.marginRight || '0') || 0;
      return sum + el.getBoundingClientRect().width + marginEnd;
    }, 0);

    let changed = false;
    if (originalsWidth > 0) {
      this.#marqueeOriginalsWidth = originalsWidth;
      const wantedSets = Math.max(1, Math.ceil(window.innerWidth / originalsWidth));
      const existingSets = 1 + Math.floor(groupA.querySelectorAll('[data-marquee-clone]').length / originals.length);
      for (let i = existingSets; i < wantedSets; i++) {
        for (const slide of originals) groupA.appendChild(this.#cloneSlide(slide));
        changed = true;
      }
    }

    let groupB = track.querySelector('[data-marquee-group="b"]');
    if (!groupB || changed) {
      groupB?.remove();
      groupB = document.createElement('div');
      groupB.className = 'announcement-bar__group';
      groupB.dataset.marqueeGroup = 'b';
      groupB.setAttribute('aria-hidden', 'true');
      groupB.setAttribute('inert', '');
      for (const slide of Array.from(groupA.children)) {
        if (slide instanceof HTMLElement) groupB.appendChild(this.#cloneSlide(slide));
      }
      track.appendChild(groupB);
    }

    // The copies changed while running (e.g. the phone was rotated): restart
    // both groups together so they stay end to end, at the same speed.
    if (changed && track.dataset.marqueeRunning === 'true') {
      this.#matchSpeed();
      for (const group of track.querySelectorAll('[data-marquee-group]')) {
        if (!(group instanceof HTMLElement)) continue;
        group.style.animationName = 'none';
        void group.offsetWidth;
        group.style.animationName = '';
      }
    }
  }

  /**
   * Same speed (pixels per second) as the marquee section further down the
   * page: its copy width divided by its duration. On pages without that
   * section, the same speed it has (1073.53px in 25s). Set before the
   * animation starts, so it never jumps.
   */
  #matchSpeed() {
    const groupA = this.#marqueeTrack?.querySelector('[data-marquee-group="a"]');
    if (!(groupA instanceof HTMLElement)) return;
    let pxPerSecond = 1073.53 / 25;
    const other = document.querySelector('marquee-component.marquee-component');
    const otherCopy = other?.querySelector('.marquee__wrapper > .marquee__content');
    const otherSeconds = other ? parseFloat(getComputedStyle(other).getPropertyValue('--marquee-speed')) : NaN;
    if (otherCopy instanceof HTMLElement && otherSeconds > 0 && otherCopy.offsetWidth > 0) {
      pxPerSecond = otherCopy.getBoundingClientRect().width / otherSeconds;
    }
    const seconds = groupA.getBoundingClientRect().width / pxPerSecond;
    if (seconds > 0) this.#marqueeTrack?.style.setProperty('--announcement-marquee-duration', `${seconds.toFixed(3)}s`);
  }

  /**
   * @param {HTMLElement} slide
   * @returns {HTMLElement}
   */
  #cloneSlide(slide) {
    const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
    clone.removeAttribute('ref');
    clone.removeAttribute('id');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.dataset.marqueeClone = 'true';
    return clone;
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
